import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { ArrowPathIcon, DocumentArrowDownIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { ChatBubbleLeftRightIcon, PaperAirplaneIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import api from '../utils/api';

// Debounce utility para la búsqueda
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Normalizar texto para búsqueda (eliminando acentos y convirtiendo a mayúsculas)
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
};

const isISODate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());

const toNumber2 = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100) / 100;
};

const getTramiteAmount = (tramite) => {
  const precomputed = toNumber2(tramite?.monto_justificar);
  if (precomputed > 0) return precomputed;
  const adjusted = toNumber2(tramite?.total_ajustado);
  const total = toNumber2(tramite?.total);
  if (adjusted > 0) return adjusted;
  if (total > 0) return total;
  return adjusted || total || 0;
};

const normalizeStatus = (value) => normalizeText(value || '').toLowerCase();

const isPendingTramite = (tramite) => normalizeStatus(tramite?.semaforo) === 'rojo';

const escapeExcelCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const isoCompare = (a, b) => String(a || '').localeCompare(String(b || ''));

const parseISOToUTCDate = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const parts = iso.split('-').map((v) => parseInt(v, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d));
};

const formatUTCDateToISO = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const getISOWeekRange = (anchorISO) => {
  const anchor = parseISOToUTCDate(anchorISO);
  if (!anchor) return { start: '', end: '' };
  const day = anchor.getUTCDay(); // 0=Dom .. 6=Sáb
  const mondayIndex = (day + 6) % 7; // Lunes=0
  const start = new Date(anchor.getTime() - mondayIndex * 86400000);
  const end = new Date(start.getTime() + 6 * 86400000);
  return { start: formatUTCDateToISO(start), end: formatUTCDateToISO(end) };
};

const getMonthRangeFromYYYYMM = (yyyyMM) => {
  if (!yyyyMM || typeof yyyyMM !== 'string' || !/^\d{4}-\d{2}$/.test(yyyyMM)) return { start: '', end: '' };
  const [yStr, mStr] = yyyyMM.split('-');
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return { start: '', end: '' };
  const start = new Date(Date.UTC(y, m - 1, 1));
  const end = new Date(Date.UTC(y, m, 0)); // día 0 del mes siguiente = último día del mes actual
  return { start: formatUTCDateToISO(start), end: formatUTCDateToISO(end) };
};

const getYearRangeFromYYYY = (yyyy) => {
  const y = parseInt(String(yyyy || ''), 10);
  if (!Number.isFinite(y) || y < 1900) return { start: '', end: '' };
  const start = new Date(Date.UTC(y, 0, 1));
  const end = new Date(Date.UTC(y, 11, 31));
  return { start: formatUTCDateToISO(start), end: formatUTCDateToISO(end) };
};

const filterDaysBetweenInclusive = (allDays, startISO, endISO) => {
  if (!Array.isArray(allDays) || allDays.length === 0) return [];
  if (!isISODate(startISO) || !isISODate(endISO)) return [];
  const start = startISO;
  const end = endISO;
  if (isoCompare(start, end) > 0) return [];
  return allDays.filter((d) => isISODate(d) && isoCompare(d, start) >= 0 && isoCompare(d, end) <= 0);
};

const TramiteList = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [days, setDays] = useState([]);
  const [pendingDays, setPendingDays] = useState([]); // Días con trámites pendientes
  const [selectedDay, setSelectedDay] = useState(''); // Día seleccionado (único)
  // Filtros por periodo (aditivo): NO altera cálculos, solo define qué días consultar
  const [periodMode, setPeriodMode] = useState('DAY'); // DAY | WEEK | MONTH | YEAR | RANGE
  const [selectedDays, setSelectedDays] = useState([]); // Lista de días (YYYY-MM-DD) a consultar en conjunto
  const [monthValue, setMonthValue] = useState(''); // YYYY-MM
  const [yearValue, setYearValue] = useState(''); // YYYY
  const [rangeStart, setRangeStart] = useState(''); // YYYY-MM-DD
  const [rangeEnd, setRangeEnd] = useState(''); // YYYY-MM-DD

  const [tramites, setTramites] = useState([]);
  const [matrizadores, setMatrizadores] = useState([]);
  const [matrizadoresData, setMatrizadoresData] = useState([]);
  const [selectedMatrizador, setSelectedMatrizador] = useState('');
  const [matrizadorSearch, setMatrizadorSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [semaforoFilter, setSemaforoFilter] = useState('ALL'); // ALL | PENDING
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalTramites, setTotalTramites] = useState(0);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false); // Estado para la confirmación de envío de correos
  const [emailRecipients, setEmailRecipients] = useState([]); // Matrizadores a notificar

  // Cargar datos de matrizadores para obtener los números de teléfono y correos
  useEffect(() => {
    const fetchMatrizadores = async () => {
      try {
        const response = await api.get('/api/audit/matrizadores/list/');
        if (Array.isArray(response.data)) {
          setMatrizadoresData(response.data);
        } else {
          toast.error('Error al cargar los datos de los matrizadores: formato de respuesta inválido.', {
            position: 'top-right',
            autoClose: 5000,
          });
          setMatrizadoresData([]);
        }
      } catch (err) {
        toast.error('Error al cargar los datos de los matrizadores.', {
          position: 'top-right',
          autoClose: 5000,
        });
        setMatrizadoresData([]);
      }
    };
    if (!loading && user) {
      fetchMatrizadores();
    }
  }, [user, loading]);

  // Determinar si el trámite es justificable (para distinguir visualmente, pero el semáforo se muestra siempre)
  const shouldShowSemaforo = (tramite) => {
    const hasComprobanteAfectado = tramite.comprobante_afectado && typeof tramite.comprobante_afectado === 'string' && tramite.comprobante_afectado.trim() !== '';
    return !(tramite.tipo === 'egreso' || hasComprobanteAfectado);
  };

  // Filtrado de trámites por matrizador y búsqueda
  const filteredTramitesMemo = useMemo(() => {
    let filtered = tramites || [];

    // Filtro por matrizador seleccionado
    if (selectedMatrizador) {
      filtered = filtered.filter((t) => normalizeText(t.usuario_matrizador) === normalizeText(selectedMatrizador));
    }

    // Filtro operativo para el notario: solo trámites pendientes / semáforo rojo
    if (semaforoFilter === 'PENDING') {
      filtered = filtered.filter(isPendingTramite);
    }

    // Filtro por búsqueda de matrizador
    if (matrizadorSearch) {
      filtered = filtered.filter((t) => normalizeText(t.usuario_matrizador || '').includes(normalizeText(matrizadorSearch)));
    }

    // Filtro por búsqueda avanzada (código o descripción)
    if (searchQuery) {
      filtered = filtered.filter((tramite) => {
        const matchesNoTemporal = normalizeText(tramite.no_temporal || '').includes(normalizeText(searchQuery));
        const matchesNoComprobante = normalizeText(tramite.no_comprobante || '').includes(normalizeText(searchQuery));
        const matchesActo = normalizeText(tramite.acto || '').includes(normalizeText(searchQuery));
        const matchesItems = (tramite.items || []).some((item) =>
          normalizeText(item.descripcion || '').includes(normalizeText(searchQuery))
        );
        return matchesNoTemporal || matchesNoComprobante || matchesActo || matchesItems;
      });
    }

    return filtered;
  }, [tramites, selectedMatrizador, semaforoFilter, matrizadorSearch, searchQuery]);

  // Resumen de trámites pendientes por matrizador (para contador y notario)
  const pendingTramitesByMatrizador = useMemo(() => {
    const summary = {};
    const pendingCount = {};
    tramites.forEach((tramite) => {
      if (tramite.semaforo === 'rojo' && tramite.tipo === 'ingreso' && !tramite.comprobante_afectado) {
        const matrizador = normalizeText(tramite.usuario_matrizador || 'Sin Matrizador');
        if (!summary[matrizador]) {
          summary[matrizador] = {
            usuario_matrizador: matrizador,
            tramites_count: 0,
            total: 0,
          };
        }
        summary[matrizador].tramites_count += 1;
        summary[matrizador].total += getTramiteAmount(tramite);
        pendingCount[matrizador] = (pendingCount[matrizador] || 0) + 1;
      }
    });
    const summaryArray = Object.values(summary).map((s) => ({
      ...s,
      total: toNumber2(s.total).toFixed(2),
    }));
    return { summary: summaryArray, pendingCount };
  }, [tramites]);

  const recentDays = useMemo(() => {
    return (Array.isArray(days) ? days : []).slice(0, 12);
  }, [days]);

  const availableYears = useMemo(() => {
    const years = new Set();
    (Array.isArray(days) ? days : []).forEach((d) => {
      if (isISODate(d)) years.add(String(d).slice(0, 4));
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [days]);

  const effectiveDays = useMemo(() => {
    if (Array.isArray(selectedDays) && selectedDays.length > 0) return selectedDays;
    if (isISODate(selectedDay)) return [selectedDay];
    return [];
  }, [selectedDays, selectedDay]);

  const selectedDaysLabel = useMemo(() => {
    if (!Array.isArray(effectiveDays) || effectiveDays.length === 0) return '';
    const sorted = [...effectiveDays].filter(isISODate).sort(isoCompare);
    if (sorted.length === 0) return '';
    if (sorted.length === 1) return sorted[0];
    return `${sorted[0]} → ${sorted[sorted.length - 1]} (${sorted.length} días)`;
  }, [effectiveDays]);


  // Validar permisos
  useEffect(() => {
    if (!loading && (!user || (!user.is_contador && !user.is_notario))) {
      toast.error('No tienes permisos para acceder a esta página.', { position: 'top-right', autoClose: 3000 });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Cargar días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await api.get('/api/audit/document-days/', {
          params: { page: 1, page_size: 1000 } // Asegurar que obtenemos todos los días
        });
        const fetchedDays = Array.isArray(response.data.days) ? response.data.days : [];
        setDays(fetchedDays);
        if (fetchedDays.length > 0) {
          // Seleccionar automáticamente el día más reciente o el día específico (2025-05-12)
          const targetDay = fetchedDays.find(day => day === '2025-05-12') || fetchedDays[0];
          setSelectedDay(targetDay);
        }
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
        setError('Error al cargar los días disponibles.');
      }
    };
    if (!loading && user && (user.is_contador || user.is_notario)) {
      fetchDays();
    }
  }, [user, loading]);

  // Cargar trámites (soporta día único o periodos/rangos sin alterar lógica de cálculos)
  const fetchTramites = useCallback(
    async (overrideDays) => {
      const daysToFetch =
        Array.isArray(overrideDays) && overrideDays.length > 0 ? overrideDays : effectiveDays;

      if (!Array.isArray(daysToFetch) || daysToFetch.length === 0) {
        toast.error('Por favor, selecciona un día o un periodo.', { position: 'top-right', autoClose: 3000 });
        return;
      }

      const invalidDay = daysToFetch.find((d) => !isISODate(d));
      if (invalidDay) {
        toast.error('La fecha debe tener formato válido YYYY-MM-DD.', { position: 'top-right', autoClose: 3000 });
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const responses = await Promise.all(
          daysToFetch.map((day) =>
            api.get('/api/audit/list/', {
              params: {
                fecha_documento: day,
                page: 1,
                page_size: 1000,
                justificable: user.is_contador,
                include_pending_days: user.is_notario,
              },
            })
          )
        );

        let totalCount = 0;
        const mergedTramites = [];
        const pendingDaysAgg = [];

        responses.forEach((response, idx) => {
          const day = daysToFetch[idx];

          if (!response.data || !Array.isArray(response.data.tramites)) {
            throw new Error('Respuesta inválida del servidor: tramites no es un array');
          }

          const mapped = response.data.tramites.map((tramite, i) => {
            const total = toNumber2(tramite.total);
            const totalAjustado = toNumber2(tramite.total_ajustado);
            const montoJustificar = totalAjustado > 0 ? totalAjustado : total;

            const stableId =
              tramite.id || `${day}-${tramite.no_temporal || 'nt'}-${tramite.no_comprobante || 'nc'}-${i}`;

            return {
              ...tramite,
              id: stableId,
              usuario_matrizador: normalizeText(tramite.usuario_matrizador || 'Sin Matrizador'),
              num_reconocimientos: parseInt(tramite.reconocimientos_count || 0, 10) || 0,
              total,
              total_ajustado: totalAjustado,
              monto_justificar: montoJustificar,
              comision_auto: parseFloat(tramite.comision_auto || 0) || 0,
              comprobante_afectado: tramite.comprobante_afectado || '',
              subtotal: parseFloat(tramite.subtotal || 0) || 0,
              iva: parseFloat(tramite.iva || 0) || 0,
              items: Array.isArray(tramite.items) ? tramite.items : [],
              semaforo: tramite.semaforo || 'rojo',
              tipo: tramite.tipo || 'ingreso',
              fecha_comprobante: tramite.fecha_comprobante || '',
              no_temporal: tramite.no_temporal || '',
              no_comprobante: tramite.no_comprobante || '',
              acto: tramite.acto || '',
              fecha_documento: tramite.fecha_documento || day,
            };
          });

          mergedTramites.push(...mapped);
          totalCount += response.data.total_tramites || mapped.length;

          if (Array.isArray(response.data.pending_days) && response.data.pending_days.length > 0) {
            pendingDaysAgg.push(...response.data.pending_days);
          }
        });

        setTramites(mergedTramites);
        setTotalTramites(totalCount || mergedTramites.length);

        if (user.is_notario && pendingDaysAgg.length > 0) {
          const byDate = {};
          pendingDaysAgg.forEach((pd) => {
            const d = pd && pd.fecha_documento ? String(pd.fecha_documento) : '';
            if (!isISODate(d)) return;

            if (!byDate[d]) {
              byDate[d] = { ...pd };
              return;
            }

            const prev = byDate[d];
            const prevOverdue = parseInt(prev.days_overdue || 0, 10) || 0;
            const nextOverdue = parseInt(pd.days_overdue || 0, 10) || 0;
            const prevCount = parseInt(prev.pending_tramites_count || 0, 10) || 0;
            const nextCount = parseInt(pd.pending_tramites_count || 0, 10) || 0;

            byDate[d] = {
              ...prev,
              days_overdue: Math.max(prevOverdue, nextOverdue),
              pending_tramites_count: prevCount + nextCount,
            };
          });

          setPendingDays(Object.values(byDate).sort((a, b) => isoCompare(b.fecha_documento, a.fecha_documento)));
        } else {
          setPendingDays([]);
        }

        // Actualizar lista de matrizadores (sin tocar cálculos)
        const uniqueMatrizadores = [...new Set(mergedTramites.map((t) => normalizeText(t.usuario_matrizador)))].sort();
        setMatrizadores(uniqueMatrizadores);
      } catch (err) {
        if (err.response) {
          setError(`Error del servidor al cargar los trámites: ${err.response.data.error || 'Error desconocido'}`);
        } else if (err.request) {
          setError('No se pudo conectar al servidor. Verifica tu conexión a internet.');
        } else {
          setError(`Error al cargar los trámites: ${err.message}`);
        }
        toast.error('Error al cargar los trámites.', { position: 'top-right', autoClose: 5000 });
        setTramites([]);
        setMatrizadores([]);
        setPendingDays([]);
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveDays, user]
  );


  // Efecto para recargar trámites cuando cambia el día seleccionado (modo día único)
  useEffect(() => {
    if (Array.isArray(selectedDays) && selectedDays.length > 0) return;
    if (isISODate(selectedDay)) {
      fetchTramites([selectedDay]);
    }
  }, [fetchTramites, selectedDay, selectedDays]);


  const handleSelectDay = useCallback((dayValue) => {
    setSelectedDay(dayValue);

    // Al seleccionar manualmente un día, se desactiva cualquier filtro por periodo
    setSelectedDays([]);
    setPeriodMode('DAY');
    setMonthValue('');
    setYearValue('');
    setRangeStart('');
    setRangeEnd('');

    setSelectedMatrizador('');
    setTramites([]);
    setMatrizadores([]);
    setError(null);
    setSearchQuery('');
    setMatrizadorSearch('');
    setSemaforoFilter('ALL');
  }, []);


  const handleDayChange = useCallback((e) => {
    handleSelectDay(e.target.value);
  }, [handleSelectDay]);

  const clearPeriodSelection = useCallback(() => {
    setSelectedDays([]);
    setPeriodMode('DAY');
    setMonthValue('');
    setYearValue('');
    setRangeStart('');
    setRangeEnd('');
  }, []);

  const applyPeriodSelection = useCallback(() => {
    // Reset filtros internos (no toca cálculos)
    setSelectedMatrizador('');
    setSearchQuery('');
    setMatrizadorSearch('');
    setSemaforoFilter('ALL');

    if (periodMode === 'DAY') {
      if (!isISODate(selectedDay)) {
        toast.error('Selecciona un día válido (YYYY-MM-DD).', { position: 'top-right', autoClose: 3000 });
        return;
      }
      const dayList = [selectedDay];
      setSelectedDays(dayList);
      fetchTramites(dayList);
      return;
    }

    let start = '';
    let end = '';

    if (periodMode === 'WEEK') {
      if (!isISODate(selectedDay)) {
        toast.error('Selecciona un día válido para calcular la semana.', { position: 'top-right', autoClose: 3000 });
        return;
      }
      const range = getISOWeekRange(selectedDay);
      start = range.start;
      end = range.end;
    } else if (periodMode === 'MONTH') {
      const ref = monthValue || (isISODate(selectedDay) ? String(selectedDay).slice(0, 7) : '');
      const range = getMonthRangeFromYYYYMM(ref);
      start = range.start;
      end = range.end;
      if (!start || !end) {
        toast.error('Selecciona un mes válido (YYYY-MM).', { position: 'top-right', autoClose: 3000 });
        return;
      }
    } else if (periodMode === 'YEAR') {
      const ref = yearValue || (isISODate(selectedDay) ? String(selectedDay).slice(0, 4) : '');
      const range = getYearRangeFromYYYY(ref);
      start = range.start;
      end = range.end;
      if (!start || !end) {
        toast.error('Selecciona un año válido.', { position: 'top-right', autoClose: 3000 });
        return;
      }
    } else if (periodMode === 'RANGE') {
      if (!isISODate(rangeStart) || !isISODate(rangeEnd)) {
        toast.error('Selecciona un rango de fechas válido (inicio y fin).', { position: 'top-right', autoClose: 3000 });
        return;
      }
      start = rangeStart;
      end = rangeEnd;

      if (isoCompare(start, end) > 0) {
        toast.error('El rango es inválido: la fecha de inicio no puede ser mayor a la fecha fin.', {
          position: 'top-right',
          autoClose: 4000,
        });
        return;
      }
    }

    const dayList = filterDaysBetweenInclusive(days, start, end);

    if (!Array.isArray(dayList) || dayList.length === 0) {
      toast.info('No hay días disponibles dentro del periodo/rango seleccionado.', { position: 'top-right', autoClose: 3500 });
      return;
    }

    // Por consistencia de UI, el día "principal" será el más reciente disponible del periodo
    setSelectedDays(dayList);
    setSelectedDay(dayList[0]);
    fetchTramites(dayList);
  }, [periodMode, selectedDay, monthValue, yearValue, rangeStart, rangeEnd, days, fetchTramites]);

  const handleMatrizadorChange = useCallback((e) => {
    const newMatrizador = e.target.value;
    setSelectedMatrizador(newMatrizador);
  }, []);

  // Debounce para la búsqueda de matrizadores
  const debouncedSetMatrizadorSearch = useMemo(
    () => debounce((value) => setMatrizadorSearch(value), 300),
    []
  );

  const handleMatrizadorSearchChange = (e) => {
    debouncedSetMatrizadorSearch(e.target.value);
  };

  // Debounce para la búsqueda avanzada
  const debouncedSetSearchQuery = useMemo(
    () => debounce((value) => setSearchQuery(value), 300),
    []
  );

  const handleSearchQueryChange = (e) => {
    debouncedSetSearchQuery(e.target.value);
  };

  const exportTramitesToXls = useCallback(() => {
    const rows = Array.isArray(filteredTramitesMemo) ? filteredTramitesMemo : [];
    if (rows.length === 0) {
      toast.info('No hay trámites para exportar con los filtros actuales.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const headers = [
      'No. Temporal',
      'No. Comprobante',
      'Fecha Comprobante',
      'Fecha Documento',
      'Matrizador',
      'Acto / Items',
      'Total Ajustado',
      'Comision Auto',
      'Semaforo',
      'Tipo',
      'Total',
      'Comprobante Afectado',
    ];

    const tableRows = rows.map((tramite) => {
      const items = Array.isArray(tramite.items)
        ? tramite.items.map((item) => item?.descripcion || '').filter(Boolean).join(' | ')
        : '';
      return [
        tramite.no_temporal || '',
        tramite.no_comprobante || '',
        tramite.fecha_comprobante || '',
        tramite.fecha_documento || '',
        tramite.usuario_matrizador || '',
        items || tramite.acto || '',
        getTramiteAmount(tramite).toFixed(2),
        parseFloat(tramite.comision_auto || 0).toFixed(2),
        tramite.semaforo || '',
        tramite.tipo || '',
        parseFloat(tramite.total || 0).toFixed(2),
        tramite.comprobante_afectado || '',
      ];
    });

    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
            th { background: #0f2a4d; color: #ffffff; font-weight: bold; }
            th, td { border: 1px solid #9ca3af; padding: 6px; vertical-align: top; }
            .num { mso-number-format:"0.00"; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>${headers.map((h) => `<th>${escapeExcelCell(h)}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${tableRows
                .map(
                  (row) =>
                    `<tr>${row
                      .map((cell, index) => `<td${[6, 7, 10].includes(index) ? ' class="num"' : ''}>${escapeExcelCell(cell)}</td>`)
                      .join('')}</tr>`
                )
                .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const filenameDate = selectedDaysLabel || selectedDay || 'tramites';
    const suffix = semaforoFilter === 'PENDING' ? 'pendientes-rojo' : 'todos';
    const filename = `informe-tramites-${suffix}-${String(filenameDate).replace(/[^0-9A-Za-z_-]+/g, '_')}.xls`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    downloadBlob(blob, filename);
  }, [filteredTramitesMemo, selectedDay, selectedDaysLabel, semaforoFilter]);

  // Enviar mensaje por WhatsApp
  const handleWhatsAppClick = (matrizadorName) => {
    const pendingCount = pendingTramitesByMatrizador.pendingCount[matrizadorName] || 0;
    if (pendingCount === 0) {
      toast.info(`No hay trámites pendientes para notificar a ${matrizadorName}.`, {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    const message = user.is_notario
      ? `Hola ${matrizadorName}, soy el notario. Tienes ${pendingCount} trámites pendientes de justificación para el día ${selectedDay}. Por favor, justifícalos lo antes posible.`
      : `Hola ${matrizadorName}, tienes ${pendingCount} trámites pendientes de justificación para el día ${selectedDay}. Por favor, justifícalos lo antes posible.`;
    const encodedMessage = encodeURIComponent(message);

    const normalizedMatrizadorName = normalizeText(matrizadorName);
    const matrizador = matrizadoresData.find((m) => normalizeText(m.nombre) === normalizedMatrizadorName);
    if (matrizador && matrizador.telefono) {
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${matrizador.telefono}&text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
    } else {
      toast.error(`No se encontró un número de teléfono para ${matrizadorName}.`, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // Preparar el envío de correos
  const prepareEmailNotification = () => {
    const matrizadoresToNotify = pendingTramitesByMatrizador.summary.filter(
      (summary) => summary.tramites_count > 0
    );
    if (matrizadoresToNotify.length === 0) {
      toast.info('No hay matrizadores con trámites pendientes para notificar.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    setEmailRecipients(matrizadoresToNotify);
    setShowEmailConfirmation(true);
  };

  // Enviar correos a los matrizadores
  const handleEmailClick = async () => {
    setShowEmailConfirmation(false);
    try {
      const response = await api.post('/api/audit/notify-matrizadores/', {
        fechas_documento: Array.isArray(effectiveDays) ? effectiveDays : [],
      });

      if (response.data.notifications_sent.length > 0) {
        toast.success(`Notificaciones enviadas a ${response.data.notifications_sent.length} matrizadores.`, {
          position: 'top-right',
          autoClose: 5000,
        });
      }
      if (response.data.notifications_failed.length > 0) {
        toast.error(
          `No se pudieron enviar notificaciones a ${response.data.notifications_failed.length} matrizadores: ${response.data.notifications_failed.map(f => `${f.matrizador} (${f.reason})`).join(', ')}`,
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      }
    } catch (err) {
      toast.error('Error al enviar las notificaciones por correo.', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setEmailRecipients([]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex flex-wrap justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Lista de Trámites</h1>
        </div>
        <Link
          to={user.is_notario ? '/dashboard/notario' : '/dashboard/contador'}
          className="mt-2 sm:mt-0 bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all"
          aria-label="Volver al dashboard"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        {/* Sección de Días con Trámites Pendientes para el Notario */}
        {pendingDays.length > 0 && user.is_notario && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Días con Trámites Pendientes</h2>
            <div className="space-y-4">
              {pendingDays
                .filter((day) => day.fecha_documento === selectedDay)
                .map((day) => (
                  <div key={day.fecha_documento} className="bg-yellow-500 bg-opacity-20 p-4 rounded-lg">
                    <p className="text-sm sm:text-base">
                      <span className="font-medium text-gray-300">Fecha:</span> {day.fecha_documento}
                    </p>
                    <p className="text-sm sm:text-base">
                      <span className="font-medium text-gray-300">Días de Retraso:</span> {day.days_overdue}
                    </p>
                    <p className="text-sm sm:text-base">
                      <span className="font-medium text-gray-300">Trámites Pendientes:</span> {day.pending_tramites_count}
                    </p>
                  </div>
                ))}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Seleccionar Día</h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
            <div className="w-full sm:w-1/2">
              <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                Día de los Trámites
              </label>
              <div className="bg-notaria-blue-2/70 border border-white/10 rounded-lg p-3">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                  <input
                    id="fecha_documento"
                    type="date"
                    value={selectedDay}
                    onChange={handleDayChange}
                    className="w-full p-2.5 rounded-lg bg-notaria-blue-1 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    aria-label="Seleccionar día de trámites"
                  />
                  <button
                    type="button"
                    onClick={() => recentDays.length > 0 && handleSelectDay(recentDays[0])}
                    className="px-3 py-2 rounded-lg text-xs sm:text-sm bg-notaria-yellow text-notaria-blue-1 font-semibold hover:bg-notaria-red hover:text-white transition-all disabled:opacity-50"
                    disabled={recentDays.length === 0}
                  >
                    Más reciente
                  </button>
                </div>

                {recentDays.length > 0 ? (
                  <div className="mt-3">
                    <p className="text-xs text-gray-300 mb-2">Accesos rápidos</p>
                    <div className="flex flex-wrap gap-2">
                      {recentDays.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleSelectDay(day)}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                            day === selectedDay
                              ? 'bg-notaria-yellow text-notaria-blue-1 border-notaria-yellow'
                              : 'bg-notaria-blue-1 border-white/10 hover:bg-notaria-yellow hover:text-notaria-blue-1'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Filtros por periodo (aditivo) */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <p className="text-sm sm:text-base font-semibold">Filtros por periodo</p>
                    {Array.isArray(selectedDays) && selectedDays.length > 1 ? (
                      <span className="text-[11px] sm:text-xs text-gray-300">
                        Periodo: <span className="text-white/90 font-semibold">{selectedDaysLabel}</span>
                      </span>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'DAY', label: 'Día' },
                      { key: 'WEEK', label: 'Semana' },
                      { key: 'MONTH', label: 'Mes' },
                      { key: 'YEAR', label: 'Año' },
                      { key: 'RANGE', label: 'Rango' },
                    ].map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setPeriodMode(m.key)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                          periodMode === m.key
                            ? 'bg-notaria-yellow text-notaria-blue-1 border-notaria-yellow'
                            : 'bg-notaria-blue-1/70 text-white border-white/10 hover:border-notaria-yellow/40 hover:bg-notaria-blue-1/90'
                        }`}
                        aria-label={`Filtrar por ${m.label}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  {/* Controles por modo */}
                  {periodMode === 'WEEK' ? (
                    <div className="mt-3 text-[11px] sm:text-xs text-gray-300">
                      {(() => {
                        const r = getISOWeekRange(selectedDay);
                        return r.start && r.end ? (
                          <span>
                            Semana (Lun–Dom):{' '}
                            <span className="text-white/90 font-semibold">{r.start} → {r.end}</span>
                          </span>
                        ) : (
                          <span>Selecciona un día válido para calcular la semana.</span>
                        );
                      })()}
                    </div>
                  ) : null}

                  {periodMode === 'MONTH' ? (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Mes</label>
                        <input
                          type="month"
                          value={monthValue || (isISODate(selectedDay) ? String(selectedDay).slice(0, 7) : '')}
                          onChange={(e) => setMonthValue(e.target.value)}
                          className="w-full p-2.5 rounded-lg bg-notaria-blue-1 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                          aria-label="Seleccionar mes"
                        />
                      </div>
                      <div className="text-[11px] sm:text-xs text-gray-300 pb-1">
                        {(() => {
                          const ref = monthValue || (isISODate(selectedDay) ? String(selectedDay).slice(0, 7) : '');
                          const r = getMonthRangeFromYYYYMM(ref);
                          return r.start && r.end ? (
                            <span>
                              Rango: <span className="text-white/90 font-semibold">{r.start} → {r.end}</span>
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ) : null}

                  {periodMode === 'YEAR' ? (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Año</label>
                        <select
                          value={yearValue || (isISODate(selectedDay) ? String(selectedDay).slice(0, 4) : '')}
                          onChange={(e) => setYearValue(e.target.value)}
                          className="w-full p-2.5 rounded-lg bg-notaria-blue-1 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                          aria-label="Seleccionar año"
                        >
                          <option value="">Seleccionar año...</option>
                          {availableYears.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="text-[11px] sm:text-xs text-gray-300 pb-1">
                        {(() => {
                          const ref = yearValue || (isISODate(selectedDay) ? String(selectedDay).slice(0, 4) : '');
                          const r = getYearRangeFromYYYY(ref);
                          return r.start && r.end ? (
                            <span>
                              Rango: <span className="text-white/90 font-semibold">{r.start} → {r.end}</span>
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ) : null}

                  {periodMode === 'RANGE' ? (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Desde</label>
                        <input
                          type="date"
                          value={rangeStart}
                          onChange={(e) => setRangeStart(e.target.value)}
                          className="w-full p-2.5 rounded-lg bg-notaria-blue-1 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                          aria-label="Fecha inicio"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-300 mb-1">Hasta</label>
                        <input
                          type="date"
                          value={rangeEnd}
                          onChange={(e) => setRangeEnd(e.target.value)}
                          className="w-full p-2.5 rounded-lg bg-notaria-blue-1 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                          aria-label="Fecha fin"
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={applyPeriodSelection}
                      className="px-3 py-2 rounded-lg text-xs sm:text-sm bg-notaria-yellow text-notaria-blue-1 font-semibold hover:bg-notaria-red hover:text-white transition-all"
                    >
                      Aplicar periodo
                    </button>
                    <button
                      type="button"
                      onClick={clearPeriodSelection}
                      className="px-3 py-2 rounded-lg text-xs sm:text-sm bg-notaria-blue-1/70 border border-white/10 text-white hover:border-notaria-yellow/40 transition-all"
                    >
                      Reset
                    </button>
                  </div>

                  {Array.isArray(selectedDays) && selectedDays.length > 1 ? (
                    <p className="mt-2 text-[11px] sm:text-xs text-gray-300">
                      Consultando: <span className="text-white/90 font-semibold">{selectedDaysLabel}</span>
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            <motion.button
              onClick={fetchTramites}
              disabled={isLoading || !isISODate(selectedDay)}
              className={`bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg hover:bg-notaria-red hover:text-white transition-all ${
                isLoading || !isISODate(selectedDay) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              whileHover={{ scale: isLoading || !isISODate(selectedDay) ? 1 : 1.02 }}
              whileTap={{ scale: isLoading || !isISODate(selectedDay) ? 1 : 0.98 }}
              aria-label="Cargar trámites"
            >
              {isLoading ? (
                <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
              ) : (
                'Cargar Trámites'
              )}
            </motion.button>
          </div>
        </motion.div>

        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-200"
          >
            Cargando trámites, por favor espera...
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-7xl mx-auto bg-red-500 text-white p-4 rounded-lg mb-6 sm:mb-8"
          >
            {error}
          </motion.div>
        ) : selectedDay && filteredTramitesMemo.length > 0 ? (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold">Lista de Trámites</h2>
              <div className="w-full sm:w-1/3 mt-2 sm:mt-0">
                <label htmlFor="usuario_matrizador" className="block text-sm sm:text-lg font-semibold mb-2">
                  Seleccionar Matrizador
                </label>
                <select
                  id="usuario_matrizador"
                  value={selectedMatrizador}
                  onChange={handleMatrizadorChange}
                  className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Seleccionar matrizador"
                >
                  <option value="">Todos los Matrizadores</option>
                  {matrizadores.map((matrizador) => (
                    <option key={matrizador} value={matrizador}>
                      {matrizador}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Sección de Trámites Pendientes por Matrizador para Contador y Notario */}
            {pendingTramitesByMatrizador.summary.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-6 relative overflow-hidden bg-gradient-to-br from-notaria-blue-2/70 to-notaria-blue-1/80 border border-white/10 p-4 sm:p-5 rounded-xl"
              >
                <div className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full bg-notaria-yellow/10 blur-2xl" />
                <div className="pointer-events-none absolute -bottom-16 -left-8 w-36 h-36 rounded-full bg-white/5 blur-2xl" />

                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <p className="text-lg font-semibold">Trámites Pendientes por Matrizador</p>
                  <span className="text-xs sm:text-sm text-gray-300">
                    {pendingTramitesByMatrizador.summary.length} matrizadores
                  </span>
                </div>

                <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 relative">
                  {pendingTramitesByMatrizador.summary.map((summary) => (
                    <div
                      key={summary.usuario_matrizador}
                      className="group bg-notaria-blue-1/55 border border-white/10 rounded-xl px-3 py-3 sm:px-4 sm:py-3.5 hover:border-notaria-yellow/40 hover:bg-notaria-blue-1/75 transition-all"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="min-w-0 flex items-start gap-3">
                          <div className="hidden sm:flex h-10 w-10 rounded-xl bg-notaria-blue-2/70 border border-white/15 items-center justify-center shrink-0">
                            <UserCircleIcon className="w-6 h-6 text-gray-200" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm sm:text-base font-semibold truncate">
                              {summary.usuario_matrizador}
                            </p>
                            <p className="text-[11px] sm:text-xs text-gray-300 mt-0.5">
                              Pendientes de justificación
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                            <span className="px-2.5 py-0.5 rounded-full bg-notaria-blue-2/80 border border-white/10 font-medium">
                              {summary.tramites_count} trámites
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full bg-notaria-yellow/20 text-notaria-yellow border border-notaria-yellow/35 font-semibold">
                              Total: ${summary.total}
                            </span>
                          </div>

                          {(user.is_contador || user.is_notario) && summary.tramites_count > 0 && (
                            <div className="flex items-center gap-2 self-start lg:self-center">
                              {/* Botón de WhatsApp */}
                              <motion.button
                                onClick={() => handleWhatsAppClick(summary.usuario_matrizador)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-green-400/35 bg-green-500/10 text-green-300 hover:bg-green-500/20 hover:text-green-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500/60"
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                aria-label={`Enviar mensaje de WhatsApp a ${summary.usuario_matrizador} sobre trámites pendientes`}
                                title={`Notificar a ${summary.usuario_matrizador} sobre trámites pendientes vía WhatsApp`}
                              >
                                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                                <span className="text-[11px] font-medium hidden sm:inline">WhatsApp</span>
                              </motion.button>

                              {/* Botón de Correo (solo para notario) */}
                              {user.is_notario && (
                                <motion.button
                                  onClick={prepareEmailNotification}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-blue-400/35 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 hover:text-blue-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/60"
                                  whileHover={{ scale: 1.04 }}
                                  whileTap={{ scale: 0.96 }}
                                  aria-label={`Enviar correo a ${summary.usuario_matrizador} sobre trámites pendientes`}
                                  title={`Notificar a ${summary.usuario_matrizador} sobre trámites pendientes vía correo`}
                                >
                                  <PaperAirplaneIcon className="w-4 h-4" />
                                  <span className="text-[11px] font-medium hidden sm:inline">Correo</span>
                                </motion.button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
            <div className="mb-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-lg border border-white/10 bg-notaria-blue-2/60 p-3">
              <div className="grid grid-cols-1 sm:grid-cols-[220px_minmax(0,1fr)] gap-3 items-end">
                <div>
                  <label htmlFor="semaforo_filter" className="block text-sm font-semibold mb-2">
                    Estado
                  </label>
                  <div className="relative">
                    <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                    <select
                      id="semaforo_filter"
                      value={semaforoFilter}
                      onChange={(e) => setSemaforoFilter(e.target.value)}
                      className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-1 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                      aria-label="Filtrar por estado de semáforo"
                    >
                      <option value="ALL">Todos</option>
                      <option value="PENDING">Pendientes / rojo</option>
                    </select>
                  </div>
                </div>

                <div className="rounded-lg bg-notaria-blue-1/70 border border-white/10 p-3">
                  <p className="text-xs text-gray-300">Resultado visible</p>
                  <p className="text-lg font-bold">
                    {filteredTramitesMemo.length} de {tramites.length} trámites
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={exportTramitesToXls}
                disabled={filteredTramitesMemo.length === 0}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Exportar informe de trámites a Excel"
                title="Exportar informe XLS"
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
                Exportar XLS
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="relative w-full">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  list="matrizadores_datalist"
                  type="text"
                  placeholder="Buscar / seleccionar matrizador..."
                  onChange={handleMatrizadorSearchChange}
                  className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Buscar o seleccionar matrizador"
                />
                <datalist id="matrizadores_datalist">
                  {matrizadores.map((matrizador) => (
                    <option key={matrizador} value={matrizador} />
                  ))}
                </datalist>
              </div>
              <div className="relative w-full">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por código o descripción..."
                  onChange={handleSearchQueryChange}
                  className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Buscar por código o descripción"
                />
              </div>
            </div>
            <div className="mb-4 bg-notaria-blue-2 p-4 rounded-lg">
              <p className="text-lg font-semibold">Resumen:</p>
              <p className="text-lg">Total de Trámites: {totalTramites}</p>
              <p className="text-sm text-gray-300">Mostrados con filtros: {filteredTramitesMemo.length}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-notaria-blue-2 text-white">
                    <th className="p-3 text-left">No. Temporal</th>
                    <th className="p-3 text-left">No. Comprobante</th>
                    <th className="p-3 text-left">Fecha Comprobante</th>
                    <th className="p-3 text-left">Acto</th>
                    <th className="p-3 text-left">Total Ajustado</th>
                    <th className="p-3 text-left">Comisión Auto</th>
                    <th className="p-3 text-left">Semáforo</th>
                    <th className="p-3 text-left">Tipo</th>
                    <th className="p-3 text-left">Total</th>
                    <th className="p-3 text-left">Comprobante Afectado</th>
                    <th className="p-3 text-left">Matrizador</th>
                    <th className="p-3 text-left">Fecha Documento</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTramitesMemo.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="p-3 text-center text-gray-400">
                        No hay trámites que coincidan con los criterios de búsqueda.
                      </td>
                    </tr>
                  ) : (
                    filteredTramitesMemo.map((tramite) => {
                      const showSemaforo = shouldShowSemaforo(tramite);
                      const tooltip = !showSemaforo
                        ? (tramite.tipo === 'egreso' ? 'Nota de crédito - No justificable' : 'Tiene comprobante afectado - No justificable')
                        : '';
                      return (
                        <motion.tr
                          key={tramite.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`border-b border-notaria-blue-2 hover:bg-notaria-blue-3 ${
                            !showSemaforo ? 'bg-gray-700 opacity-75' : ''
                          }`}
                        >
                          <td className="p-3">{tramite.no_temporal || '-'}</td>
                          <td className="p-3">{tramite.no_comprobante || '-'}</td>
                          <td className="p-3">{tramite.fecha_comprobante || '-'}</td>
                          <td className="p-3">
                            <div className="max-h-[80px] overflow-y-auto text-sm">
                              {(tramite.items || []).map((item, index) => (
                                <div key={index} className="py-0.5">{item.descripcion || '-'}</div>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">${getTramiteAmount(tramite).toFixed(2)}</td>
                          <td className="p-3">${parseFloat(tramite.comision_auto || 0).toFixed(2)}</td>
                          <td className="p-3" title={tooltip}>
                            <span
                              className={`inline-block w-4 h-4 rounded-full ${
                                tramite.semaforo === 'rojo'
                                  ? 'bg-red-500'
                                  : tramite.semaforo === 'amarillo'
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              aria-label={`Estado del semáforo: ${tramite.semaforo}`}
                            ></span>
                          </td>
                          <td className="p-3">{tramite.tipo.charAt(0).toUpperCase() + tramite.tipo.slice(1)}</td>
                          <td className="p-3">${parseFloat(tramite.total || 0).toFixed(2)}</td>
                          <td className="p-3">{tramite.comprobante_afectado || '-'}</td>
                          <td className="p-3">{tramite.usuario_matrizador || '-'}</td>
                          <td className="p-3">{tramite.fecha_documento || '-'}</td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : selectedDay ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-400"
          >
            No hay trámites disponibles para el día seleccionado.
          </motion.div>
        ) : null}
      </main>

      {/* Modal de Confirmación de Envío de Correos */}
      {showEmailConfirmation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-notaria-blue-1 bg-opacity-95 p-6 rounded-2xl shadow-2xl w-full max-w-md"
          >
            <h3 className="text-xl sm:text-2xl font-semibold mb-4">Confirmar Envío de Correos</h3>
            <p className="mb-4">
              Se enviarán notificaciones por correo a <strong>{emailRecipients.length}</strong> matrizadores con trámites pendientes para el día / periodo seleccionado: <strong>{selectedDaysLabel || selectedDay || '-'}</strong>.
            </p>
            <p className="mb-6">¿Estás seguro de que deseas proceder?</p>
            <div className="flex justify-end space-x-4">
              <motion.button
                onClick={() => setShowEmailConfirmation(false)}
                className="bg-gray-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-600 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Cancelar envío de correos"
              >
                Cancelar
              </motion.button>
              <motion.button
                onClick={handleEmailClick}
                className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg hover:bg-notaria-red hover:text-white transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Confirmar envío de correos"
              >
                Enviar
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default TramiteList;
